import type {
  ApplicationReason,
  Gender,
  MaritalStatus,
  OutstandingRequirement,
} from '@/config/constants'

/** The NPR record returned by §4.5 create-application, in domain shape. */
export interface PersonalDetails {
  idNo: string
  fullForenames: string
  surname: string
  initials: string
  dateOfBirth: string
  placeOfBirth?: string
  /**
   * Optional because both are rendered through `t('common:gender.<value>')`, and a
   * value with no catalogue entry renders the key itself — the citizen would read
   * the literal text "gender.". Absent is renderable: the row is dropped. Empty is
   * not. §4.5 sends an explicit `null` for anything the NPR record lacks.
   */
  gender?: Gender
  maritalStatus?: MaritalStatus
  nationality: string
  countryOfBirth: string
  maidenName?: string
}

/** A product the applicant is eligible for, from §4.1. */
export interface EligibleProduct {
  productCategory: string
  productType: string
  description: string
  eligible: boolean
  ineligibleReason?: string
  /** Omitted by the spec — not empty — when the product is ineligible. */
  reasons?: ApplicationReason[]
}

/**
 * `officer` and `physicalVerification` are the VFS-centre steps: an officer signs in
 * at the counter, then confirms the applicant's in-person biometric enrolment and
 * document checks. They sit between the address and the declaration because the
 * citizen may only sign once that in-person check has passed.
 *
 * `review` precedes `declaration` deliberately: the citizen checks what is about
 * to be sent, and only then signs. That makes the declaration the last step and
 * therefore the point of no return — the confirm modal lives there.
 */
export const WIZARD_STEPS = [
  'details',
  'contact',
  'address',
  'officer',
  'physicalVerification',
  'review',
  'declaration',
] as const
export type WizardStep = (typeof WIZARD_STEPS)[number]

/**
 * The full ten-step journey, and exactly the rows the sidebar draws.
 * WIZARD_STEPS remains the API-backed subset whose order the DHA contract
 * enforces; `idNumber` in front of it is the entry screen where the reference
 * number is minted, and `submitted` behind it is the receipt.
 */
export const FLOW_STEPS = ['idNumber', 'face', ...WIZARD_STEPS, 'submitted'] as const
export type FlowStep = (typeof FLOW_STEPS)[number]

/**
 * What the citizen submitted at §4.6 and §4.7.
 *
 * Both endpoints answer 204 and neither has a GET, so without keeping a copy the
 * Review & Submit screen could not show what is about to be sent — the citizen
 * would be asked to confirm details the app had already forgotten. Structural
 * types rather than the features' own zod outputs: `application-session` is the
 * one slice everything imports, so it must not import any of them back.
 */
export interface ContactSnapshot {
  emailAddress: string
  mobileNo: string
  alternativeContactNo?: string
}

export interface AddressSnapshot {
  unitNo?: string
  complex?: string
  streetNo?: string
  streetName: string
  suburb?: string
  city?: string
  province?: string
  postalCode: string
}

/**
 * The VFS officer who signed in at step 6, from Microsoft Entra ID.
 *
 * Declared here rather than imported from `lib/auth`, for the same reason
 * ContactSnapshot and AddressSnapshot are structural: `application-session` is the
 * one slice everything imports, so it must not import anything back.
 *
 * `agentId` is the §4.10 `AgentID`, which is String(35). That limit is what chooses
 * the source — the user principal name fits, the account object ID (a 36-character
 * GUID) does not. See docs/api-integration.md §6.
 */
export interface OfficerSnapshot {
  agentId: string
  name: string
  username: string
  oid?: string
  jobTitle?: string
  /** Candidate for the §4.8 BranchCode, unconfirmed — captured to answer it. */
  officeLocation?: string
}

/**
 * One line of the §4.10 payment breakdown.
 *
 * Lives here rather than in `finalisation/api` because the ESLint boundaries rules
 * forbid one feature importing another's `api/`, and this is the slice every
 * feature is allowed to import. A future payment screen needs the same shape.
 */
export interface PaymentItem {
  description: string
  amount: number
  isPenalty: boolean
}

export interface SessionState {
  // ---------------------------------------------------------------- persisted
  appRefNo?: string
  /** Step 1 output. Persistence is gated with personalDetails (ADR 0008). */
  idNo?: string
  /** Set on successful finalise; feeds the Submitted screen's date tile. */
  submittedAt?: string
  /** From §4.1. No endpoint re-reads this, so losing it means starting over. */
  availableProducts?: EligibleProduct[]
  /** From §4.5. Likewise unrecoverable — there is no GET for it. */
  personalDetails?: PersonalDetails
  /**
   * Held as plain codes, not narrowed to DECLARATION_CODE: §4.5 names the set and
   * §4.17 supplies the text, so a code we have never heard of is DHA's to define,
   * not ours to drop. See createApplication.mapper.ts.
   */
  declarationsRequired?: string[]
  reason?: ApplicationReason
  /** §4.6 output, for the Review & Submit summary. */
  contact?: ContactSnapshot
  /** §4.7 output, for the Review & Submit summary. */
  address?: AddressSnapshot
  /**
   * Set at step 6 once the officer has signed in. Feeds §4.10's `AgentID`, which is
   * mandatory on four collection-phase endpoints, so it has to outlive the
   * sign-in redirect and any refresh between here and finalise.
   */
  officer?: OfficerSnapshot
  completedSteps: WizardStep[]

  /**
   * VFS Client §4.6 step 6 output — the row recording which officer signed in against this
   * application, and the key its verification outcome is posted to at step 7.
   *
   * Persisted for two reasons at once: signing in navigates the whole page to
   * Microsoft and back, and the outcome is not known until either the officer rejects
   * the application or finalise succeeds — several steps and any number of refreshes
   * later.
   */
  vacAuthId?: number
  /**
   * Guards a second Passed outcome. The receipt fires the write on arrival, and a
   * refresh of that screen re-runs the effect.
   */
  vfsClientVerificationSaved?: boolean

  faceAttempts: number
  faceVerified: boolean
  faceBlocked: boolean
  /** ISO timestamp of the last interaction, for the inactivity TTL. */
  lastActivityAt?: string
  /**
   * Set when `expireIfStale()` drops a session the TTL has caught, and cleared by
   * the ID-number screen once it has shown the citizen why they lost their place.
   *
   * The expiry signal lives HERE rather than in `location.state`, which is what it
   * used to ride on. A route's state is a message about one navigation, and four
   * ways back to step 1 carry none — the header logo, MSAL's postLogoutRedirectUri
   * return, a bookmarked `/apply`, and the `*` catch-all — so an expired session
   * arriving by any of them was never cleared and sat in sessionStorage with the
   * sidebar still drawing green ticks for an application the citizen could no
   * longer open. It also survives F5 through `history.state.usr`, which made the
   * banner sticky across reloads.
   */
  expiredAt?: string

  /**
   * The §4.10 finalise result.
   *
   * Finalise is the only call that reports what the citizen still owes and what
   * still stands between them and their card, and — like §4.6 and §4.7 — nothing
   * reads it back. Holding it here is what lets the receipt screen, and any later
   * payment screen, say something more useful than "submitted".
   *
   * `outstandingRequirements` is normally `['PAYMENT', 'BIO_ENROLMENT']`; until
   * §5.1 payment-confirmation is wired, the application stalls on the first of
   * those. See docs/api-integration.md §7.
   */
  outstandingRequirements?: OutstandingRequirement[]
  totalPayable?: number
  paymentItems?: PaymentItem[]

  // ------------------------------------------------------ NEVER persisted
  /**
   * Biometric data. Special personal information under POPIA s26, and held in
   * memory only — the persistence allow-list deliberately omits both, and a unit
   * test asserts they never reach sessionStorage.
   */
  photoImg?: string
  signatureImg?: string
}
